import React, { useState, useEffect, useMemo, useRef } from "react";
import clsx from "clsx";
import styles from "./ModalCreateOrder.module.scss";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { useSnackbar } from "notistack";
import donationApi from "../../../apis/donationApi";
import shippingFeeApi from "../../../apis/shippingFeeApi";
import recycledProductApi from "../../../apis/recycledProductApi";
import axiosDonation from "../../../apis/axiosDonation";
import PROVINCE from "../../../constants/province";
import DISTRICTS from "../../../constants/district";
import WARD from "../../../constants/ward";
import { PACKAGE_TYPE_VER_2, COLLECT_TIME } from "../../../constants/constant";
import { API_URL_DONATION } from "../../../utils/setting";
import StorageService from "../../../services/StorageService";

export interface ModalCreateOrderProps {
  className?: string;
  open: boolean;
  handleClose: () => void;
  reload: () => void;
  initialType?: string;
}

const DONATE_AMOUNTS = [10000, 20000, 50000, 100000, 200000, 500000];

const ModalCreateOrder = (props: ModalCreateOrderProps) => {
  const { className, open, handleClose, reload, initialType } = props;
  const { enqueueSnackbar } = useSnackbar();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [fastFeeAmount, setFastFeeAmount] = useState<number>(10000);
  const [formData, setFormData] = useState({
    customerName: "",
    customerPhone: "",
    customerEmail: "",
    customerNote: "",
    collectDate: "",
    collectTimeIndex: 0,
    provinceId: "",
    districtId: "",
    wardId: "",
    address: "",
    shippingTypeId: "11123",
    shipperType: "fast",
    paymentMethod: "offline",
  });

  const [servicesState, setServicesState] = useState({
    normal: {
      active: true,
      weight: "456",
      charity: true,
      cyclic_processing: true,
      environmental_protection: false
    },
    recycle: {
      active: false,
      weight: "456",
      extend_life_cycle: true,
      buy_recycled_products: false,
      donate_money: false,
      boughtProducts: {} as Record<string, number>,
      donateAmount: 0
    },
    zero_waste: {
      active: false,
      weight: "456",
      reduce_emissions: true,
      energy_recovery: false
    }
  });

  const [fee, setFee] = useState({
    totalPrice: 0,
    shippingPrice: 0,
    productsPrice: 0,
    zeroWastePrice: 0,
    promotionalPrice: 0
  });

  useEffect(() => {
    if (open) {
      let pId = "01G4866BBJGYR1JH8E6RPYEW2C";
      const partnerDataStr = localStorage.getItem("partnerData");
      if (partnerDataStr) {
        try {
          const p = JSON.parse(partnerDataStr);
          if (p.id || p.partnerId) pId = p.id || p.partnerId;
        } catch (e) {}
      } else if (process.env.REACT_APP_ENV === "production") {
        pId = "01G486CQAV7VW2FK3DHZV1T2FG";
      }
      shippingFeeApi.getFastShippingFee({ partnerId: pId }).then((res) => {
        if (res?.data?.code === 0 && res?.data?.data?.fee !== undefined) {
          setFastFeeAmount(res.data.data.fee);
        }
      }).catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      setServicesState({
        normal: {
          active: initialType === "donation" || !initialType,
          weight: "456",
          charity: true,
          cyclic_processing: true,
          environmental_protection: false
        },
        recycle: {
          active: initialType === "recycle",
          weight: "456",
          extend_life_cycle: true,
          buy_recycled_products: true,
          donate_money: false,
          donateAmount: DONATE_AMOUNTS[0],
          boughtProducts: {}
        },
        zero_waste: {
          active: initialType === "zero_waste",
          weight: "456",
          reduce_emissions: true,
          energy_recovery: true
        }
      });
      setFormData({
        customerName: "",
        customerPhone: "",
        customerEmail: "",
        customerNote: "",
        collectDate: "",
        collectTimeIndex: 0,
        provinceId: "",
        districtId: "",
        wardId: "",
        address: "",
        shippingTypeId: "11123",
        shipperType: "fast",
        paymentMethod: "offline",
      });
      setPictures([]);
    }
  }, [open, initialType]);

  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [pictures, setPictures] = useState<string[]>([]);

  // Only show HCM
  const availableProvinces = useMemo(() => {
    return PROVINCE.filter((p: any) => p.code === "202");
  }, []);

  const availableDistricts = useMemo(() => {
    return DISTRICTS.filter((d: any) => d.province_code === formData.provinceId);
  }, [formData.provinceId]);

  const availableWards = useMemo(() => {
    return WARD.filter((w: any) => w.district_code === formData.districtId);
  }, [formData.districtId]);

  useEffect(() => {
    if (open) {
      let pId = "01G4866BBJGYR1JH8E6RPYEW2C";
      const partnerDataStr = localStorage.getItem("partnerData");
      if (partnerDataStr) {
        try {
          const p = JSON.parse(partnerDataStr);
          if (p.id || p.partnerId) pId = p.id || p.partnerId;
        } catch (e) {}
      } else if (process.env.REACT_APP_ENV === "production") {
        pId = "01G486CQAV7VW2FK3DHZV1T2FG";
      }
      shippingFeeApi.getFastShippingFee({ partnerId: pId }).then((res) => {
        if (res?.data?.code === 0 && res?.data?.data?.fee !== undefined) {
          setFastFeeAmount(res.data.data.fee);
        }
      }).catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      fetchProducts();
    }
  }, [open]);

  const fetchProducts = async () => {
    try {
      const res = await recycledProductApi.getList({
        filter: {},
        paging: { limit: 100, offset: 0 }
      });
      if (res?.data?.data?.products) {
        setProducts(res.data.data.products);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const [anchorElService, setAnchorElService] = useState<null | HTMLElement>(null);
  const handleOpenAddService = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElService(event.currentTarget);
  };
  const handleCloseAddService = () => {
    setAnchorElService(null);
  };
  const handleAddService = (type: string) => {
    handleServiceChange(type, "active", true);
    handleCloseAddService();
  };

  const handleChangeForm = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "provinceId" && { districtId: "", wardId: "" }),
      ...(name === "districtId" && { wardId: "" }),
    }));
  };

  const handleServiceChange = (serviceName: string, field: string, value: any) => {
    setServicesState(prev => ({
      ...prev,
      [serviceName]: {
        ...prev[serviceName as keyof typeof prev],
        [field]: value
      }
    }));
  };

  const handleProductQtyChange = (id: string, delta: number) => {
    setServicesState(prev => {
      const currentQty = prev.recycle.boughtProducts[id] || 0;
      const newQty = Math.max(0, currentQty + delta);
      const newBoughtProducts = { ...prev.recycle.boughtProducts };
      if (newQty === 0) {
        delete newBoughtProducts[id];
      } else {
        newBoughtProducts[id] = newQty;
      }
      return {
        ...prev,
        recycle: {
          ...prev.recycle,
          boughtProducts: newBoughtProducts
        }
      };
    });
  };

  const handleUploadImages = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    if (pictures.length + files.length > 3) {
      enqueueSnackbar("Chỉ được tải lên tối đa 3 ảnh", { variant: "warning" });
      return;
    }

    setIsUploading(true);
    try {
      const newPics: string[] = [];
      for (let i = 0; i < files.length; i++) {
        const formDataUpload = new FormData();
        formDataUpload.append("file", files[i]);
        const uploadRes = await axiosDonation.post("/serene/upload", formDataUpload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const relativeUrl = uploadRes.data?.data?.url;
        const baseUrl = (API_URL_DONATION || "").replace(/\/$/, "");
        const url = relativeUrl ? `${baseUrl}${relativeUrl}` : "";
        if (url) {
          newPics.push(url);
        }
      }
      setPictures((prev) => [...prev, ...newPics]);
    } catch (error: any) {
      console.log(error);
      enqueueSnackbar("Lỗi upload ảnh", { variant: "error" });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const buildServicesPayload = () => {
    const result: any[] = [];

    const pushNormal = () => {
      if (servicesState.normal.active) {
        const forms = [];
        if (servicesState.normal.charity) forms.push("charity");
        if (servicesState.normal.cyclic_processing) forms.push("cyclic_processing");
        if (servicesState.normal.environmental_protection) forms.push("environmental_protection");
        result.push({
          fieldId: "4641",
          serviceId: "123",
          donationType: "normal",
          packageTypeId: servicesState.normal.weight,
          donationForms: forms,
          items: pictures.length > 0 ? { picture: pictures } : undefined
        });
      }
    };

    const pushRecycle = () => {
      if (servicesState.recycle.active) {
        const forms = [];
        if (servicesState.recycle.extend_life_cycle) forms.push("extend_life_cycle");
        if (servicesState.recycle.buy_recycled_products) forms.push("buy_recycled_products");
        if (servicesState.recycle.donate_money) forms.push("donate_money");

        const buyProducts = Object.entries(servicesState.recycle.boughtProducts).map(([id, qty]) => {
          const p = products.find(p => p.id === id);
          return { id, quantity: qty, name: p?.name, price: p?.price, image: p?.image };
        });

        result.push({
          fieldId: "4641",
          serviceId: "123",
          donationType: "recycle",
          packageTypeId: servicesState.recycle.weight,
          donationForms: forms,
          buyProducts: buyProducts.length > 0 ? buyProducts : undefined,
          donationAmount: servicesState.recycle.donate_money ? servicesState.recycle.donateAmount : undefined,
          items: pictures.length > 0 ? { picture: pictures } : undefined
        });
      }
    };

    const pushZeroWaste = () => {
      if (servicesState.zero_waste.active) {
        const forms = [];
        if (servicesState.zero_waste.reduce_emissions) forms.push("reduce_emissions");
        if (servicesState.zero_waste.energy_recovery) forms.push("energy_recovery");
        result.push({
          fieldId: "4641",
          serviceId: "123",
          donationType: "zero_waste",
          packageTypeId: servicesState.zero_waste.weight,
          donationForms: forms,
          items: pictures.length > 0 ? { picture: pictures } : undefined
        });
      }
    };

    // Push the primary service first
    if (initialType === "donation") pushNormal();
    else if (initialType === "recycle") pushRecycle();
    else if (initialType === "zero_waste") pushZeroWaste();

    // Push the rest
    if (initialType !== "donation") pushNormal();
    if (initialType !== "recycle") pushRecycle();
    if (initialType !== "zero_waste") pushZeroWaste();

    return result;
  };

  useEffect(() => {
    if (!open) return;
    const timeoutId = setTimeout(() => {
      calculateFee();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [servicesState, formData.shippingTypeId, formData.shipperType, formData.provinceId, formData.districtId, formData.wardId]);

  const calculateFee = async () => {
    const services = buildServicesPayload();
    if (services.length === 0) {
      setFee({ totalPrice: 0, shippingPrice: 0, productsPrice: 0, zeroWastePrice: 0, promotionalPrice: 0 });
      return;
    }

    // Only calculate if enough data is provided
    if (formData.shippingTypeId === "11123" && (!formData.provinceId || !formData.districtId || !formData.wardId)) return;

    setIsCalculating(true);
    try {


      let pId = "01G4866BBJGYR1JH8E6RPYEW2C"; // fallback
      const partnerDataStr = localStorage.getItem("partnerData");
      if (partnerDataStr) {
        try {
          const pd = JSON.parse(partnerDataStr);
          if (pd && pd.id) {
            pId = pd.id;
          }
        } catch (e) { }
      }
      let warehouseAddress = {
        provinceID: "202",
        districtID: "1463",
        wardID: "21806",
        address: "44 đường 24",
      };
      const whData = localStorage.getItem("warehouseData");
      if (whData) {
        try {
          const parsed = JSON.parse(whData);
          if (parsed && parsed.address) {
            warehouseAddress = {
              provinceID: parsed.address.provinceId || parsed.address.provinceID,
              districtID: parsed.address.districtId || parsed.address.districtID,
              wardID: parsed.address.wardId || parsed.address.wardID,
              address: parsed.address.address.toLowerCase() === "44 đường 24" ? "44 đường 24" : parsed.address.address,
            };
          }
        } catch (e) { }
      }

      const payload = {
        partnerId: pId,
        services,
        shippingTypeId: formData.shippingTypeId,
        shipperType: formData.shippingTypeId === "11123" ? (formData.shipperType || "fast") : "saving",
        shippingTypeAddress: warehouseAddress,
        customerProvinceId: formData.shippingTypeId === "11123" ? formData.provinceId : warehouseAddress.provinceID,
        customerDistrictId: formData.shippingTypeId === "11123" ? formData.districtId : warehouseAddress.districtID,
        customerWardId: formData.shippingTypeId === "11123" ? formData.wardId : warehouseAddress.wardID
      };

      console.log("PAYLOAD calculateFeeV2:", JSON.stringify(payload, null, 2));
      const res = await donationApi.calculateFeeV2(payload);
      console.log("Calculated fee response:", res);
      if (res?.data?.code === 0 && res?.data?.data) {
        if (res.data.data.fastShippingFee !== undefined) {
          setFastFeeAmount(res.data.data.fastShippingFee);
        }
        setFee({
          totalPrice: res.data.data.totalPrice || 0,
          shippingPrice: res.data.data.shippingPrice || 0,
          productsPrice: res.data.data.productsPrice || 0,
          zeroWastePrice: res.data.data.zeroWastePrice || 0,
          promotionalPrice: res.data.data.promotionalPrice || 0,
        });
      } else {
        console.error("API failed:", res?.data);
        enqueueSnackbar("Lỗi tính phí: " + (res?.data?.message || "Unknown error"), { variant: "error" });
      }
    } catch (e: any) {
      console.error("Lỗi tính phí:", e);
      enqueueSnackbar("Lỗi tính phí: " + (e.response?.data?.message || e.message), { variant: "error" });
    } finally {
      setIsCalculating(false);
    }
  };

  const handleSubmit = async () => {
    const services = buildServicesPayload();
    if (services.length === 0) {
      enqueueSnackbar("Vui lòng chọn ít nhất 1 dịch vụ", { variant: "warning" });
      return;
    }
    if (
      !formData.customerName ||
      !formData.customerPhone ||
      (formData.shippingTypeId === "11123" && (
        !formData.provinceId ||
        !formData.districtId ||
        !formData.wardId ||
        !formData.address ||
        !formData.collectDate
      ))
    ) {
      enqueueSnackbar("Vui lòng điền đầy đủ thông tin bắt buộc", { variant: "warning" });
      return;
    }

    setLoading(true);
    try {


      let pId = process.env.REACT_APP_ENV === "production" ? "01G486CQAV7VW2FK3DHZV1T2FG" : "01G4866BBJGYR1JH8E6RPYEW2C";
      
      let warehouseAddress = {
        provinceID: "202",
        districtID: "1463",
        wardID: "21806",
        address: "44 đường 24",
      };
      const whData = localStorage.getItem("warehouseData");
      if (whData) {
        try {
          const parsed = JSON.parse(whData);
          if (parsed && parsed.address) {
            warehouseAddress = {
              provinceID: parsed.address.provinceId || parsed.address.provinceID,
              districtID: parsed.address.districtId || parsed.address.districtID,
              wardID: parsed.address.wardId || parsed.address.wardID,
              address: parsed.address.address.toLowerCase() === "44 đường 24" ? "44 đường 24" : parsed.address.address,
            };
          }
        } catch (e) { }
      }

      let currentUserId = "admin_manual_" + Date.now().toString();
      try {
        const tokenRaw = StorageService.token;
        if (tokenRaw) {
          let tokenStr = tokenRaw;
          try {
            const parsed = JSON.parse(tokenRaw);
            if (typeof parsed === "string") tokenStr = parsed;
            else if (parsed.token) tokenStr = parsed.token;
          } catch (e) { }
          if (tokenStr && tokenStr.includes(".")) {
            const payloadStr = window.atob(tokenStr.split(".")[1]);
            const payload = JSON.parse(payloadStr);
            if (payload && payload.sub) {
              currentUserId = payload.sub;
            }
          }
        }
      } catch (e) {
        console.error("Parse token error", e);
      }

      const payload = {
        partnerId: pId,
        services,
        shippingTypeId: formData.shippingTypeId,
        shipperType: formData.shippingTypeId === "11123" ? (formData.shipperType || "fast") : "saving",
        shippingTypeAddress: warehouseAddress,
        customerProvinceId: formData.shippingTypeId === "11123" ? formData.provinceId : warehouseAddress.provinceID,
        customerDistrictId: formData.shippingTypeId === "11123" ? formData.districtId : warehouseAddress.districtID,
        customerWardId: formData.shippingTypeId === "11123" ? formData.wardId : warehouseAddress.wardID,

        customerId: currentUserId,
        customerName: formData.customerName,
        customerPhone: formData.customerPhone,
        customerEmail: formData.customerEmail,
        customerNote: formData.customerNote,
        collectDate: formData.shippingTypeId === "11123" && formData.collectDate
          ? new Date(formData.collectDate).toISOString()
          : new Date(new Date().setDate(new Date().getDate() + 1)).toISOString(),
        collectFromTime: formData.shippingTypeId === "11123"
          ? COLLECT_TIME[formData.collectTimeIndex].collectFromTime
          : COLLECT_TIME[0].collectFromTime,
        collectToTime: formData.shippingTypeId === "11123"
          ? COLLECT_TIME[formData.collectTimeIndex].collectToTime
          : COLLECT_TIME[0].collectToTime,
        customerAddress: {
          provinceID: formData.provinceId || warehouseAddress.provinceID,
          districtID: formData.districtId || warehouseAddress.districtID,
          wardID: formData.wardId || warehouseAddress.wardID,
          address: formData.address || warehouseAddress.address,
        },
        paymentMethod: fee.totalPrice > 0 ? formData.paymentMethod : "",
      };

      const res = await donationApi.createDonation(payload);
      if (res?.data?.code === 0 || res?.data?.data) {
        enqueueSnackbar("Tạo đơn hàng thành công", { variant: "success" });
        handleClose();
        reload();
      } else {
        enqueueSnackbar(res?.data?.message || "Lỗi tạo đơn hàng", { variant: "error" });
      }
    } catch (error: any) {
      console.log(error);
      enqueueSnackbar(error?.response?.data?.message || "Lỗi tạo đơn hàng", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box className={clsx(styles.root, className && className)}>
        <div className={styles.header}>
          <Typography variant="h5" className={styles.title}>Tạo đơn hàng mới</Typography>
          <IconButton onClick={handleClose}><DeleteIcon /></IconButton>
        </div>

        <div className={styles.contentWrap}>
          <div className={styles.leftCol}>

            {/* Dịch vụ */}
            <div className={styles.sectionBox} style={{ display: "flex", flexDirection: "column" }}>
              <Typography className={styles.sectionTitle} style={{ order: -1 }}>Dịch vụ</Typography>

              {/* Cho Tặng */}
              {servicesState.normal.active && (
                <div style={{ order: initialType === "donation" || !initialType ? 1 : 2, marginBottom: "20px", border: "1px solid #ddd", borderRadius: "8px", padding: "15px" }}>
                  <FormControlLabel
                    control={<Checkbox checked={servicesState.normal.active} onChange={(e) => handleServiceChange("normal", "active", e.target.checked)} />}
                    label={<Typography className={styles.serviceHeader}>🎁 Cho tặng</Typography>}
                  />
                  <div style={{ marginLeft: "30px", marginTop: "10px" }}>
                    <Typography variant="body2" style={{ marginBottom: "10px" }}>Bạn có thể chọn một hoặc nhiều mục đích bên dưới:</Typography>
                    <div className={styles.checkboxGroup}>
                      <FormControlLabel control={<Checkbox size="small" checked={servicesState.normal.charity} onChange={(e) => handleServiceChange("normal", "charity", e.target.checked)} />} label="Hoạt động thiện nguyện" />
                      <FormControlLabel control={<Checkbox size="small" checked={servicesState.normal.cyclic_processing} onChange={(e) => handleServiceChange("normal", "cyclic_processing", e.target.checked)} />} label="Giao toàn quyền cho REshare xử lý" />
                      <FormControlLabel control={<Checkbox size="small" checked={servicesState.normal.environmental_protection} onChange={(e) => handleServiceChange("normal", "environmental_protection", e.target.checked)} />} label="Ủy quyền cho REshare sử dụng vào mục đích cho tặng" />
                    </div>
                    <div className={styles.weightGroup}>
                      <div className={styles.weightTitle}>Chọn trọng lượng túi đồ:</div>
                      <div className={styles.weightOptions}>
                        {PACKAGE_TYPE_VER_2.map(pt => (
                          <div key={pt.packageTypeId}
                            className={clsx(styles.weightBtn, servicesState.normal.weight === pt.packageTypeId && styles.active)}
                            onClick={() => handleServiceChange("normal", "weight", pt.packageTypeId)}>
                            {pt.label}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Tái chế */}
              {servicesState.recycle.active && (
                <div style={{ order: initialType === "recycle" ? 1 : 2, marginBottom: "20px", border: "1px solid #ddd", borderRadius: "8px", padding: "15px" }}>
                  <FormControlLabel
                    control={<Checkbox checked={servicesState.recycle.active} onChange={(e) => handleServiceChange("recycle", "active", e.target.checked)} />}
                    label={<Typography className={styles.serviceHeader}>♻️ Tái chế</Typography>}
                  />
                  <div style={{ marginLeft: "30px", marginTop: "10px" }}>
                    <div className={styles.checkboxGroup}>
                      <FormControlLabel control={<Checkbox size="small" checked={servicesState.recycle.extend_life_cycle} onChange={(e) => handleServiceChange("recycle", "extend_life_cycle", e.target.checked)} />} label="Tăng vòng đời (sửa chữa)" />

                      <FormControlLabel control={<Checkbox size="small" checked={servicesState.recycle.buy_recycled_products} onChange={(e) => handleServiceChange("recycle", "buy_recycled_products", e.target.checked)} />} label="Mua lại sản phẩm tái chế" />
                      {servicesState.recycle.buy_recycled_products && (
                        <div className={styles.productsGrid}>
                          {products.map(p => (
                            <div key={p.id} className={clsx(styles.productCard, servicesState.recycle.boughtProducts[p.id] > 0 && styles.active)}
                              onClick={() => { if (!servicesState.recycle.boughtProducts[p.id]) handleProductQtyChange(p.id, 1); }}>
                              <img src={p.image || ""} alt={p.name} className={styles.prodImg} />
                              <div className={styles.prodInfo}>
                                <div className={styles.prodName}>{p.name}</div>
                                <div className={styles.prodPrice}>{p.price?.toLocaleString()}đ</div>
                              </div>
                              {servicesState.recycle.boughtProducts[p.id] > 0 && (
                                <div className={styles.qtyControl} onClick={(e) => e.stopPropagation()}>
                                  <button onClick={() => handleProductQtyChange(p.id, -1)}>-</button>
                                  <span>{servicesState.recycle.boughtProducts[p.id]}</span>
                                  <button onClick={() => handleProductQtyChange(p.id, 1)}>+</button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      <FormControlLabel control={<Checkbox size="small" checked={servicesState.recycle.donate_money} onChange={(e) => handleServiceChange("recycle", "donate_money", e.target.checked)} />} label="Đồng hành ủng hộ REshare phát triển tái chế" />
                      {servicesState.recycle.donate_money && (
                        <div className={styles.donateAmountGroup}>
                          <Typography variant="body2">Chọn mức giá trị bạn muốn đóng góp:</Typography>
                          <div className={styles.amountOptions}>
                            {DONATE_AMOUNTS.map(amt => (
                              <div key={amt} className={clsx(styles.amountBtn, servicesState.recycle.donateAmount === amt && styles.active)}
                                onClick={() => handleServiceChange("recycle", "donateAmount", amt)}>
                                {amt.toLocaleString()}đ
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className={styles.weightGroup}>
                      <div className={styles.weightTitle}>Chọn trọng lượng túi đồ:</div>
                      <div className={styles.weightOptions}>
                        {PACKAGE_TYPE_VER_2.map(pt => (
                          <div key={pt.packageTypeId}
                            className={clsx(styles.weightBtn, servicesState.recycle.weight === pt.packageTypeId && styles.active)}
                            onClick={() => handleServiceChange("recycle", "weight", pt.packageTypeId)}>
                            {pt.label}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Xử lý */}
              {servicesState.zero_waste.active && (
                <div style={{ order: initialType === "zero_waste" ? 1 : 2, marginBottom: "20px", border: "1px solid #ddd", borderRadius: "8px", padding: "15px" }}>
                  <FormControlLabel
                    control={<Checkbox checked={servicesState.zero_waste.active} onChange={(e) => handleServiceChange("zero_waste", "active", e.target.checked)} />}
                    label={<Typography className={styles.serviceHeader}>🗑️ Xử lý</Typography>}
                  />
                  <div style={{ marginLeft: "30px", marginTop: "10px" }}>
                    <div className={styles.checkboxGroup}>
                      <FormControlLabel control={<Checkbox size="small" checked={servicesState.zero_waste.reduce_emissions} onChange={(e) => handleServiceChange("zero_waste", "reduce_emissions", e.target.checked)} />} label="Giảm phát thải" />
                      <FormControlLabel control={<Checkbox size="small" checked={servicesState.zero_waste.energy_recovery} onChange={(e) => handleServiceChange("zero_waste", "energy_recovery", e.target.checked)} />} label="Thu hồi năng lượng" />
                    </div>
                    <div className={styles.weightGroup}>
                      <div className={styles.weightTitle}>Chọn trọng lượng túi đồ:</div>
                      <div className={styles.weightOptions}>
                        {PACKAGE_TYPE_VER_2.map(pt => (
                          <div key={pt.packageTypeId}
                            className={clsx(styles.weightBtn, servicesState.zero_waste.weight === pt.packageTypeId && styles.active)}
                            onClick={() => handleServiceChange("zero_waste", "weight", pt.packageTypeId)}>
                            {pt.label}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div style={{ order: 99, display: "flex", justifyContent: "flex-end", marginBottom: "20px" }}>
                <Button variant="contained" color="success" onClick={handleOpenAddService} endIcon={<KeyboardArrowDownIcon />} style={{ textTransform: "none", fontWeight: "bold" }}>Thêm dịch vụ</Button>
                <Menu anchorEl={anchorElService} open={Boolean(anchorElService)} onClose={handleCloseAddService}>
                  {!servicesState.normal.active && <MenuItem onClick={() => handleAddService("normal")}>🎁 Cho tặng</MenuItem>}
                  {!servicesState.recycle.active && <MenuItem onClick={() => handleAddService("recycle")}>♻️ Tái chế</MenuItem>}
                  {!servicesState.zero_waste.active && <MenuItem onClick={() => handleAddService("zero_waste")}>🗑️ Xử lý</MenuItem>}
                </Menu>
              </div>
            </div>

            {/* Thông tin khách hàng & Vận chuyển */}
            <div className={styles.sectionBox}>
              <Typography className={styles.sectionTitle}>Thông tin người gửi & Vận chuyển</Typography>
              <div className={styles.inputGroup}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px" }}>
                  <TextField required name="customerName" label="Họ và tên" value={formData.customerName} onChange={handleChangeForm} fullWidth />
                  <TextField required name="customerPhone" label="Số điện thoại" value={formData.customerPhone} onChange={handleChangeForm} fullWidth />
                  <TextField name="customerEmail" label="Email" value={formData.customerEmail} onChange={handleChangeForm} fullWidth />
                </div>

                <TextField select required name="shippingTypeId" label="Phương thức lấy hàng" value={formData.shippingTypeId} onChange={handleChangeForm} fullWidth>
                  <MenuItem value="11123">Shipper đến thu gom tận nơi</MenuItem>
                  <MenuItem value="11124">Gửi trực tiếp đến kho REshare</MenuItem>
                  <MenuItem value="11125">Gửi túi đồ tại các trạm thu gom của REshare</MenuItem>
                </TextField>

                {formData.shippingTypeId === "11123" && (
                  <TextField select required name="shipperType" label="Hình thức giao hàng" value={formData.shipperType} onChange={handleChangeForm} fullWidth>
                    <MenuItem value="fast">Hỏa tốc (Giao nhanh +{fastFeeAmount.toLocaleString("vi")}đ)</MenuItem>
                    <MenuItem value="saving">Tiết kiệm</MenuItem>
                  </TextField>
                )}

                {formData.shippingTypeId === "11123" && (
                  <>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px" }}>
                      <TextField required type="date" name="collectDate" label="Ngày đến lấy" value={formData.collectDate} onChange={handleChangeForm} InputLabelProps={{ shrink: true }} inputProps={{ min: formData.shipperType === 'fast' ? new Date().toISOString().split('T')[0] : new Date(new Date().setDate(new Date().getDate() + 1)).toISOString().split('T')[0] }} fullWidth />
                      <TextField select required name="collectTimeIndex" label="Khung giờ" value={formData.collectTimeIndex} onChange={handleChangeForm} fullWidth>
                        {COLLECT_TIME.map((t, idx) => (
                          <MenuItem key={idx} value={idx}>{t.label} ({t.collectFromTime.slice(0, 5)} - {t.collectToTime.slice(0, 5)})</MenuItem>
                        ))}
                      </TextField>
                    </div>

                    <div style={{ display: "flex", gap: "10px" }}>
                      <TextField select required name="provinceId" label="Tỉnh/Thành phố" value={formData.provinceId} onChange={handleChangeForm} fullWidth>
                        {availableProvinces.map((p: any) => <MenuItem key={p.code} value={p.code}>{p.label}</MenuItem>)}
                      </TextField>
                      <TextField select required name="districtId" label="Quận/Huyện" value={formData.districtId} onChange={handleChangeForm} fullWidth disabled={!formData.provinceId}>
                        {availableDistricts.map((d: any) => <MenuItem key={d.code} value={d.code}>{d.name}</MenuItem>)}
                      </TextField>
                    </div>

                    <div style={{ display: "flex", gap: "10px" }}>
                      <TextField select required name="wardId" label="Phường/Xã" value={formData.wardId} onChange={handleChangeForm} fullWidth disabled={!formData.districtId}>
                        {availableWards.map((w: any) => <MenuItem key={w.code} value={w.code}>{w.name}</MenuItem>)}
                      </TextField>
                      <TextField required name="address" label="Địa chỉ chi tiết (Số nhà, tên đường)" value={formData.address} onChange={handleChangeForm} fullWidth />
                    </div>
                  </>
                )}

                <TextField name="customerNote" label="Ghi chú" value={formData.customerNote} onChange={handleChangeForm} fullWidth multiline rows={2} />

                <div style={{ marginTop: "10px" }}>
                  <Typography variant="body2" style={{ marginBottom: "10px", fontWeight: 500 }}>Hình ảnh túi đồ (Tối đa 3 ảnh):</Typography>
                  <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "10px" }}>
                    {pictures.map((pic, idx) => (
                      <div key={idx} style={{ position: "relative", width: "80px", height: "80px", border: "1px solid #ddd", borderRadius: "4px", overflow: "hidden" }}>
                        <img src={pic} alt="bag" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        <IconButton size="small" style={{ position: "absolute", top: 0, right: 0, backgroundColor: "rgba(255,255,255,0.7)" }} onClick={() => setPictures(prev => prev.filter((_, i) => i !== idx))}>
                          <DeleteIcon fontSize="small" color="error" />
                        </IconButton>
                      </div>
                    ))}
                  </div>
                  {pictures.length < 3 && (
                    <Button variant="outlined" component="label" disabled={isUploading}>
                      {isUploading ? "Đang tải ảnh..." : "Upload hình ảnh"}
                      <input type="file" hidden accept="image/*" multiple ref={fileInputRef} onChange={handleUploadImages} />
                    </Button>
                  )}
                </div>
              </div>
            </div>

          </div>

          <div className={styles.rightCol}>
            <div className={styles.summaryBox}>
              <Typography variant="h6" style={{ fontWeight: 600, marginBottom: "20px", color: "#00a651" }}>Tổng kết đơn hàng</Typography>

              <div className={styles.summaryRow}>
                <span>Phí ship thu gom:</span>
                <span>{fee.shippingPrice.toLocaleString()}đ</span>
              </div>
              <div className={styles.summaryRow}>
                <span>Phí xử lý (Zero waste):</span>
                <span>{fee.zeroWastePrice.toLocaleString()}đ</span>
              </div>
              <div className={styles.summaryRow}>
                <span>Sản phẩm tái chế:</span>
                <span>{fee.productsPrice.toLocaleString()}đ</span>
              </div>
              {servicesState.recycle.active && servicesState.recycle.donate_money && servicesState.recycle.donateAmount > 0 && (
                <div className={styles.summaryRow}>
                  <span>Đồng hành ủng hộ:</span>
                  <span>{servicesState.recycle.donateAmount.toLocaleString()}đ</span>
                </div>
              )}
              {fee.promotionalPrice > 0 && (
                <div className={styles.summaryRow}>
                  <span>Giảm giá:</span>
                  <span style={{ color: "red" }}>-{fee.promotionalPrice.toLocaleString()}đ</span>
                </div>
              )}

              <div className={clsx(styles.summaryRow, styles.total)}>
                <span>Tổng thanh toán:</span>
                <span className={styles.totalPrice}>
                  {fee.totalPrice.toLocaleString()}đ
                </span>
              </div>
              {isCalculating && <Typography variant="caption" color="textSecondary" style={{ display: "block", textAlign: "right" }}>Đang tính phí...</Typography>}
              
              {fee.totalPrice > 0 && (
                <div style={{ marginTop: "20px", borderTop: "1px solid #eaeaea", paddingTop: "20px" }}>
                  <Typography variant="body2" style={{ marginBottom: "10px", fontWeight: 600 }}>Phương thức thanh toán *</Typography>
                  <TextField select name="paymentMethod" value={formData.paymentMethod} onChange={handleChangeForm} fullWidth size="small" variant="outlined">
                    <MenuItem value="offline">Thanh toán tiền mặt</MenuItem>
                    <MenuItem value="momo">Ví MoMo</MenuItem>
                    <MenuItem value="vnPay">VNPAY</MenuItem>
                  </TextField>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className={styles.footer}>
          <Button variant="contained" className={styles.btnCancel} onClick={handleClose} disabled={loading || isUploading}>
            Hủy
          </Button>
          <Button variant="contained" className={styles.btnSubmit} onClick={handleSubmit} disabled={loading || isUploading || isCalculating}>
            {loading ? "Đang xử lý..." : "Xác nhận tạo đơn"}
          </Button>
        </div>
      </Box>
    </Modal>
  );
};

export default ModalCreateOrder;

